import { useState, useEffect, useCallback } from "react";
import { RotateCcw, Coins } from "lucide-react";
import { toast } from "sonner";

// ==================== SHARED COMPONENTS FOR ALL CARD POKER GAMES ====================

// Card Component
export const Card = ({ card, faceDown = false, size = "normal", selected = false, onClick }) => {
  const sizeClasses = {
    small: "w-10 h-14 text-xs",
    normal: "w-12 h-16 text-sm",
    large: "w-14 h-20 text-base"
  };

  if (!card || faceDown) {
    return (
      <div 
        className={`${sizeClasses[size]} rounded-lg bg-gradient-to-br from-blue-800 to-blue-900 border-2 border-blue-600 shadow-lg flex items-center justify-center cursor-pointer`}
        onClick={onClick}
      >
        <div className="text-blue-400 opacity-50 text-lg">♠</div>
      </div>
    );
  }

  const suits = { hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' };
  const suitSymbol = suits[card.suit] || card.suit;
  const isRed = card.suit === 'hearts' || card.suit === 'diamonds';

  return (
    <div 
      className={`${sizeClasses[size]} rounded-lg bg-white border-2 ${selected ? 'border-yellow-400 ring-2 ring-yellow-400' : 'border-gray-300'} shadow-lg flex flex-col items-center justify-center font-bold cursor-pointer ${isRed ? 'text-red-600' : 'text-gray-900'}`}
      onClick={onClick}
    >
      <span>{card.rank}</span>
      <span className="text-lg">{suitSymbol}</span>
    </div>
  );
};

// Create a deck
export const createDeck = () => {
  const suits = ['hearts', 'diamonds', 'clubs', 'spades'];
  const ranks = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
  const deck = [];
  for (const suit of suits) {
    for (const rank of ranks) {
      deck.push({ suit, rank });
    }
  }
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
};

// Get hand rank
export const getHandRank = (cards) => {
  if (!cards || cards.length < 5) return { rank: 0, name: "High Card" };
  
  const ranks = cards.map(c => {
    if (c.rank === 'A') return 14;
    if (c.rank === 'K') return 13;
    if (c.rank === 'Q') return 12;
    if (c.rank === 'J') return 11;
    return parseInt(c.rank);
  }).sort((a, b) => b - a);
  
  const suits = cards.map(c => c.suit);
  const isFlush = suits.every(s => s === suits[0]);
  const isStraight = ranks.every((r, i) => i === 0 || ranks[i-1] - r === 1) ||
                     (ranks[0] === 14 && ranks[1] === 5 && ranks[2] === 4 && ranks[3] === 3 && ranks[4] === 2);
  
  const counts = {};
  ranks.forEach(r => counts[r] = (counts[r] || 0) + 1);
  const countValues = Object.values(counts).sort((a, b) => b - a);
  
  if (isFlush && isStraight && ranks[0] === 14) return { rank: 9, name: "Royal Flush" };
  if (isFlush && isStraight) return { rank: 8, name: "Straight Flush" };
  if (countValues[0] === 4) return { rank: 7, name: "Four of a Kind" };
  if (countValues[0] === 3 && countValues[1] === 2) return { rank: 6, name: "Full House" };
  if (isFlush) return { rank: 5, name: "Flush" };
  if (isStraight) return { rank: 4, name: "Straight" };
  if (countValues[0] === 3) return { rank: 3, name: "Three of a Kind" };
  if (countValues[0] === 2 && countValues[1] === 2) return { rank: 2, name: "Two Pair" };
  if (countValues[0] === 2) return { rank: 1, name: "One Pair" };
  return { rank: 0, name: "High Card" };
};

// Compare hands
export const compareHands = (hand1, hand2) => {
  const rank1 = getHandRank(hand1);
  const rank2 = getHandRank(hand2);
  if (rank1.rank > rank2.rank) return 1;
  if (rank1.rank < rank2.rank) return -1;
  return 0;
};

// Chip Stack Display
export const ChipStack = ({ amount, label }) => (
  <div className="flex items-center gap-1">
    <div className="flex -space-x-1">
      <div className="w-4 h-4 bg-red-600 rounded-full border border-white/50" />
      <div className="w-4 h-4 bg-blue-600 rounded-full border border-white/50" />
      <div className="w-4 h-4 bg-green-600 rounded-full border border-white/50" />
    </div>
    <span className="text-yellow-400 font-bold text-sm">{amount}</span>
    {label && <span className="text-white/70 text-xs">{label}</span>}
  </div>
);

// Game Header
export const GameHeader = ({ title, chips, onNewGame }) => (
  <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700">
    <span className="text-yellow-400 font-bold text-sm">{title}</span>
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1 bg-black/30 px-2 py-1 rounded">
        <Coins className="w-4 h-4 text-yellow-400" />
        <span className="text-white font-bold text-sm">{chips?.toLocaleString() || 0}</span>
      </div>
      {onNewGame && (
        <button onClick={onNewGame} className="p-1.5 bg-blue-600 hover:bg-blue-500 rounded text-white">
          <RotateCcw className="w-4 h-4" />
        </button>
      )}
    </div>
  </div>
);

// Dealer Badge
export const DealerBadge = ({ phase, gameName }) => (
  <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 bg-gradient-to-b from-yellow-600 to-yellow-800 rounded-lg px-3 py-1 border-2 border-yellow-400">
    <div className="flex items-center gap-2">
      <span className="text-lg">👩‍💼</span>
      <div className="flex flex-col">
        <span className="text-white text-[10px] font-bold">{gameName || 'DEALER'}</span>
        <span className="text-yellow-200 text-[9px]">{phase?.toUpperCase() || 'WAITING'}</span>
      </div>
    </div>
  </div>
);

// Result Banner
export const ResultBanner = ({ result, message }) => {
  if (!result) return null;
  return (
    <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 px-6 py-3 rounded-xl shadow-2xl ${
      result === 'win' ? 'bg-gradient-to-r from-green-600 to-green-500' :
      result === 'lose' ? 'bg-gradient-to-r from-red-600 to-red-500' :
      'bg-gradient-to-r from-gray-600 to-gray-500'
    }`}>
      <div className="text-center text-white">
        <div className="text-2xl font-bold">
          {result === 'win' ? '🎉 YOU WIN!' : result === 'lose' ? '😢 YOU LOSE' : '🤝 TIE'}
        </div>
        {message && <div className="text-sm">{message}</div>}
      </div>
    </div>
  );
};

// Action Buttons - OUTSIDE the table
export const ActionButtons = ({ actions, disabled }) => (
  <div className="flex items-center justify-center gap-2 flex-wrap">
    {actions.map((action, i) => (
      <button
        key={i}
        onClick={action.onClick}
        disabled={disabled || action.disabled}
        className={`px-4 py-2 rounded-lg font-bold text-sm shadow-lg transition-all ${
          action.color === 'red' ? 'bg-red-600 hover:bg-red-500 text-white' :
          action.color === 'green' ? 'bg-green-600 hover:bg-green-500 text-white' :
          action.color === 'yellow' ? 'bg-yellow-600 hover:bg-yellow-500 text-white' :
          action.color === 'blue' ? 'bg-blue-600 hover:bg-blue-500 text-white' :
          'bg-gray-600 hover:bg-gray-500 text-white'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {action.label}
      </button>
    ))}
  </div>
);

// Oval Poker Table - Clean with nothing on it
export const PokerTable = ({ children }) => (
  <div className="relative w-full h-full bg-gray-800 overflow-hidden">
    {/* Oval Table */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[85%] rounded-[50%] bg-gradient-to-b from-green-700 to-green-900 border-8 border-amber-700 shadow-[0_0_40px_rgba(0,0,0,0.5),inset_0_0_40px_rgba(0,0,0,0.3)]">
      {/* Inner border */}
      <div className="absolute inset-4 border-2 border-amber-600/30 rounded-[50%]" />
      {children}
    </div>
  </div>
);

// ==================== 5-CARD DRAW ====================
export const FiveCardDraw = ({ API, token, onClose }) => {
  const [deck, setDeck] = useState([]);
  const [playerHand, setPlayerHand] = useState([]);
  const [dealerHand, setDealerHand] = useState([]);
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [phase, setPhase] = useState("betting");
  const [bet, setBet] = useState(0);
  const [chips, setChips] = useState(1000);
  const [result, setResult] = useState(null);
  const [showDealer, setShowDealer] = useState(false);

  const startNewHand = useCallback(() => {
    const newDeck = createDeck();
    setDeck(newDeck);
    setPlayerHand([]);
    setDealerHand([]);
    setSelectedCards(new Set());
    setPhase("betting");
    setResult(null);
    setShowDealer(false);
    setBet(0);
  }, []);

  useEffect(() => { startNewHand(); }, [startNewHand]);

  const placeBet = (amount) => {
    if (amount > chips) { toast.error("Not enough chips!"); return; }
    setBet(amount);
    setChips(prev => prev - amount);
  };

  const deal = () => {
    if (bet <= 0) { toast.error("Place a bet first!"); return; }
    const newDeck = [...deck];
    setPlayerHand(newDeck.splice(0, 5));
    setDealerHand(newDeck.splice(0, 5));
    setDeck(newDeck);
    setPhase("draw");
  };

  const toggleCard = (index) => {
    if (phase !== "draw") return;
    const newSelected = new Set(selectedCards);
    if (newSelected.has(index)) newSelected.delete(index);
    else if (newSelected.size < 3) newSelected.add(index);
    setSelectedCards(newSelected);
  };

  const drawCards = () => {
    const newDeck = [...deck];
    const newHand = playerHand.map((card, i) => 
      selectedCards.has(i) ? newDeck.pop() : card
    );
    setPlayerHand(newHand);
    setDeck(newDeck);
    setSelectedCards(new Set());
    showdown(newHand);
  };

  const showdown = (finalHand) => {
    setShowDealer(true);
    setPhase("showdown");
    const comparison = compareHands(finalHand, dealerHand);
    if (comparison > 0) {
      setResult({ type: 'win', message: `${getHandRank(finalHand).name} beats ${getHandRank(dealerHand).name}!` });
      setChips(prev => prev + bet * 2);
    } else if (comparison < 0) {
      setResult({ type: 'lose', message: `${getHandRank(dealerHand).name} beats ${getHandRank(finalHand).name}` });
    } else {
      setResult({ type: 'tie', message: "It's a tie!" });
      setChips(prev => prev + bet);
    }
  };

  const playerRank = getHandRank(playerHand);
  const dealerRank = getHandRank(dealerHand);

  return (
    <div className="w-full h-full flex flex-col">
      <GameHeader title="🎴 5-CARD DRAW" chips={chips} onNewGame={startNewHand} />
      
      {/* TABLE AREA - Only cards here */}
      <div className="flex-1 relative">
        <PokerTable>
          <DealerBadge phase={phase} gameName="5-CARD DRAW" />
          
          {/* Dealer Hand - Top */}
          <div className="absolute top-16 left-1/2 -translate-x-1/2">
            <div className="flex gap-1">
              {dealerHand.length > 0 ? dealerHand.map((card, i) => (
                <Card key={i} card={showDealer ? card : null} faceDown={!showDealer} size="normal" />
              )) : Array(5).fill(0).map((_, i) => <Card key={i} faceDown size="normal" />)}
            </div>
            {showDealer && <div className="text-yellow-300 text-xs text-center mt-1">{dealerRank.name}</div>}
          </div>
          
          {/* Pot */}
          {bet > 0 && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="bg-black/50 rounded-full px-4 py-2">
                <ChipStack amount={bet * 2} label="POT" />
              </div>
            </div>
          )}
          
          <ResultBanner result={result?.type} message={result?.message} />
          
          {/* Player Hand - Bottom */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <div className="flex gap-1">
              {playerHand.length > 0 ? playerHand.map((card, i) => (
                <Card key={i} card={card} size="large" selected={selectedCards.has(i)} onClick={() => toggleCard(i)} />
              )) : Array(5).fill(0).map((_, i) => <Card key={i} faceDown size="large" />)}
            </div>
            {playerHand.length > 0 && <div className="text-green-300 text-xs text-center mt-1">{playerRank.name}</div>}
            {phase === "draw" && <div className="text-gray-300 text-xs text-center mt-1">Click cards to discard (max 3)</div>}
          </div>
          
          {/* Player Badge */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
            <div className="text-xs font-bold text-green-400">★ YOU ★</div>
          </div>
        </PokerTable>
      </div>
      
      {/* CONTROLS - Below table */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-3 border-t border-gray-700">
        {phase === "betting" && (
          <div className="space-y-2">
            <div className="flex gap-2 justify-center flex-wrap">
              {[10, 25, 50, 100].map(amount => (
                <button key={amount} onClick={() => placeBet(amount)} disabled={amount > chips}
                  className={`px-3 py-1.5 rounded font-bold text-xs ${amount > chips ? 'bg-gray-700 text-gray-500' : bet === amount ? 'bg-yellow-500 text-black' : 'bg-purple-600 hover:bg-purple-500 text-white'}`}
                >{amount}</button>
              ))}
            </div>
            {bet > 0 && <button onClick={deal} className="w-full py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white font-bold">DEAL</button>}
          </div>
        )}
        {phase === "draw" && (
          <ActionButtons actions={[
            { label: `Draw ${selectedCards.size}`, color: "yellow", onClick: drawCards },
            { label: "Stand Pat", color: "blue", onClick: () => { setSelectedCards(new Set()); drawCards(); } }
          ]} />
        )}
        {phase === "showdown" && (
          <button onClick={startNewHand} className="w-full py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white font-bold flex items-center justify-center gap-2">
            <RotateCcw className="w-4 h-4" /> NEW HAND
          </button>
        )}
      </div>
    </div>
  );
};

// ==================== 5-CARD STUD ====================
export const FiveCardStud = ({ API, token, onClose }) => {
  const [deck, setDeck] = useState([]);
  const [playerCards, setPlayerCards] = useState([]);
  const [dealerCards, setDealerCards] = useState([]);
  const [phase, setPhase] = useState("betting");
  const [bet, setBet] = useState(0);
  const [chips, setChips] = useState(1000);
  const [result, setResult] = useState(null);

  const startNewHand = useCallback(() => {
    setDeck(createDeck());
    setPlayerCards([]);
    setDealerCards([]);
    setPhase("betting");
    setResult(null);
    setBet(0);
  }, []);

  useEffect(() => { startNewHand(); }, [startNewHand]);

  const placeBet = (amount) => {
    if (amount > chips) { toast.error("Not enough chips!"); return; }
    setBet(amount);
    setChips(prev => prev - amount);
  };

  const dealNextCard = () => {
    if (playerCards.length >= 5) {
      showdown();
      return;
    }
    const newDeck = [...deck];
    const pCard = newDeck.pop();
    const dCard = newDeck.pop();
    setDeck(newDeck);
    setPlayerCards(prev => [...prev, pCard]);
    setDealerCards(prev => [...prev, dCard]);
    if (playerCards.length === 0) setPhase("playing");
  };

  const showdown = () => {
    setPhase("showdown");
    const comparison = compareHands(playerCards, dealerCards);
    if (comparison > 0) {
      setResult({ type: 'win', message: `${getHandRank(playerCards).name}!` });
      setChips(prev => prev + bet * 2);
    } else if (comparison < 0) {
      setResult({ type: 'lose', message: `Dealer: ${getHandRank(dealerCards).name}` });
    } else {
      setResult({ type: 'tie', message: "Push!" });
      setChips(prev => prev + bet);
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      <GameHeader title="🎴 5-CARD STUD" chips={chips} onNewGame={startNewHand} />
      
      <div className="flex-1 relative">
        <PokerTable>
          <DealerBadge phase={phase} gameName="5-CARD STUD" />
          
          {/* Dealer */}
          <div className="absolute top-16 left-1/2 -translate-x-1/2">
            <div className="flex gap-1">
              {dealerCards.map((card, i) => (
                <Card key={i} card={i === 0 && phase !== "showdown" ? null : card} faceDown={i === 0 && phase !== "showdown"} size="normal" />
              ))}
              {dealerCards.length === 0 && Array(5).fill(0).map((_, i) => <Card key={i} faceDown size="normal" />)}
            </div>
            {phase === "showdown" && <div className="text-yellow-300 text-xs text-center mt-1">{getHandRank(dealerCards).name}</div>}
          </div>
          
          {bet > 0 && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="bg-black/50 rounded-full px-4 py-2">
                <ChipStack amount={bet * 2} label="POT" />
              </div>
            </div>
          )}
          
          <ResultBanner result={result?.type} message={result?.message} />
          
          {/* Player */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <div className="flex gap-1">
              {playerCards.map((card, i) => <Card key={i} card={card} size="large" />)}
              {playerCards.length === 0 && Array(5).fill(0).map((_, i) => <Card key={i} faceDown size="large" />)}
            </div>
            {playerCards.length >= 5 && <div className="text-green-300 text-xs text-center mt-1">{getHandRank(playerCards).name}</div>}
          </div>
          
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
            <div className="text-xs font-bold text-green-400">★ YOU ★</div>
          </div>
        </PokerTable>
      </div>
      
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-3 border-t border-gray-700">
        {phase === "betting" && (
          <div className="space-y-2">
            <div className="flex gap-2 justify-center flex-wrap">
              {[10, 25, 50, 100].map(amount => (
                <button key={amount} onClick={() => placeBet(amount)} disabled={amount > chips}
                  className={`px-3 py-1.5 rounded font-bold text-xs ${amount > chips ? 'bg-gray-700 text-gray-500' : bet === amount ? 'bg-yellow-500 text-black' : 'bg-purple-600 hover:bg-purple-500 text-white'}`}
                >{amount}</button>
              ))}
            </div>
            {bet > 0 && <button onClick={dealNextCard} className="w-full py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white font-bold">DEAL</button>}
          </div>
        )}
        {phase === "playing" && (
          <ActionButtons actions={[
            { label: playerCards.length < 5 ? "Deal Next Card" : "Showdown", color: "green", onClick: dealNextCard }
          ]} />
        )}
        {phase === "showdown" && (
          <button onClick={startNewHand} className="w-full py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white font-bold flex items-center justify-center gap-2">
            <RotateCcw className="w-4 h-4" /> NEW HAND
          </button>
        )}
      </div>
    </div>
  );
};

// ==================== 7-CARD STUD ====================
export const SevenCardStud = ({ API, token, onClose }) => {
  const [deck, setDeck] = useState([]);
  const [playerCards, setPlayerCards] = useState([]);
  const [dealerCards, setDealerCards] = useState([]);
  const [phase, setPhase] = useState("betting");
  const [bet, setBet] = useState(0);
  const [chips, setChips] = useState(1000);
  const [result, setResult] = useState(null);

  const startNewHand = useCallback(() => {
    setDeck(createDeck());
    setPlayerCards([]);
    setDealerCards([]);
    setPhase("betting");
    setResult(null);
    setBet(0);
  }, []);

  useEffect(() => { startNewHand(); }, [startNewHand]);

  const placeBet = (amount) => {
    if (amount > chips) { toast.error("Not enough chips!"); return; }
    setBet(amount);
    setChips(prev => prev - amount);
  };

  const dealNextCard = () => {
    if (playerCards.length >= 7) {
      showdown();
      return;
    }
    const newDeck = [...deck];
    const pCard = newDeck.pop();
    const dCard = newDeck.pop();
    setDeck(newDeck);
    setPlayerCards(prev => [...prev, pCard]);
    setDealerCards(prev => [...prev, dCard]);
    if (playerCards.length === 0) setPhase("playing");
  };

  const getBest5 = (cards) => {
    if (cards.length < 5) return cards;
    let best = null;
    let bestRank = -1;
    for (let i = 0; i < cards.length; i++) {
      for (let j = i + 1; j < cards.length; j++) {
        const hand = cards.filter((_, idx) => idx !== i && idx !== j);
        const rank = getHandRank(hand);
        if (rank.rank > bestRank) {
          bestRank = rank.rank;
          best = hand;
        }
      }
    }
    return best || cards.slice(0, 5);
  };

  const showdown = () => {
    setPhase("showdown");
    const playerBest = getBest5(playerCards);
    const dealerBest = getBest5(dealerCards);
    const comparison = compareHands(playerBest, dealerBest);
    if (comparison > 0) {
      setResult({ type: 'win', message: `${getHandRank(playerBest).name}!` });
      setChips(prev => prev + bet * 2);
    } else if (comparison < 0) {
      setResult({ type: 'lose', message: `Dealer: ${getHandRank(dealerBest).name}` });
    } else {
      setResult({ type: 'tie', message: "Push!" });
      setChips(prev => prev + bet);
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      <GameHeader title="🎴 7-CARD STUD" chips={chips} onNewGame={startNewHand} />
      
      <div className="flex-1 relative">
        <PokerTable>
          <DealerBadge phase={phase} gameName="7-CARD STUD" />
          
          {/* Dealer - first 2 face down, rest face up */}
          <div className="absolute top-16 left-1/2 -translate-x-1/2">
            <div className="flex gap-0.5">
              {dealerCards.map((card, i) => (
                <Card key={i} card={(i < 2 && phase !== "showdown") ? null : card} faceDown={i < 2 && phase !== "showdown"} size="small" />
              ))}
              {dealerCards.length === 0 && Array(7).fill(0).map((_, i) => <Card key={i} faceDown size="small" />)}
            </div>
            {phase === "showdown" && <div className="text-yellow-300 text-xs text-center mt-1">{getHandRank(getBest5(dealerCards)).name}</div>}
          </div>
          
          {bet > 0 && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="bg-black/50 rounded-full px-4 py-2">
                <ChipStack amount={bet * 2} label="POT" />
              </div>
            </div>
          )}
          
          <ResultBanner result={result?.type} message={result?.message} />
          
          {/* Player - first 2 face down (hole cards), rest face up */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <div className="flex gap-0.5">
              {playerCards.map((card, i) => <Card key={i} card={card} size="normal" />)}
              {playerCards.length === 0 && Array(7).fill(0).map((_, i) => <Card key={i} faceDown size="normal" />)}
            </div>
            {playerCards.length >= 5 && <div className="text-green-300 text-xs text-center mt-1">{getHandRank(getBest5(playerCards)).name}</div>}
          </div>
          
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
            <div className="text-xs font-bold text-green-400">★ YOU ★</div>
          </div>
        </PokerTable>
      </div>
      
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-3 border-t border-gray-700">
        {phase === "betting" && (
          <div className="space-y-2">
            <div className="flex gap-2 justify-center flex-wrap">
              {[10, 25, 50, 100].map(amount => (
                <button key={amount} onClick={() => placeBet(amount)} disabled={amount > chips}
                  className={`px-3 py-1.5 rounded font-bold text-xs ${amount > chips ? 'bg-gray-700 text-gray-500' : bet === amount ? 'bg-yellow-500 text-black' : 'bg-purple-600 hover:bg-purple-500 text-white'}`}
                >{amount}</button>
              ))}
            </div>
            {bet > 0 && <button onClick={dealNextCard} className="w-full py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white font-bold">DEAL</button>}
          </div>
        )}
        {phase === "playing" && (
          <ActionButtons actions={[
            { label: playerCards.length < 7 ? `Deal Card ${playerCards.length + 1}/7` : "Showdown", color: "green", onClick: dealNextCard }
          ]} />
        )}
        {phase === "showdown" && (
          <button onClick={startNewHand} className="w-full py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white font-bold flex items-center justify-center gap-2">
            <RotateCcw className="w-4 h-4" /> NEW HAND
          </button>
        )}
      </div>
    </div>
  );
};

export default { FiveCardDraw, FiveCardStud, SevenCardStud };
